/**
 * Re-usable period selector used everywhere reports take a date range:
 * Analytics → Money / Expenses / Trends / Reviews, Reminders → Stats,
 * Master profile, Client profile, etc.
 *
 * Renders "Цей місяць", past months back to DATA_START_MONTH, "Весь час"
 * and a "Свій" chip. Tapping "Свій" expands two native date inputs (from / to) under
 * the row. Custom dates persist when re-opening so the user can iterate.
 *
 * The component is intentionally controlled — caller owns `value` + the
 * `onChange` callback. The custom range is delivered as ISO date strings
 * (YYYY-MM-DD → ISO at 00:00 / 23:59 Kyiv local).
 */

import { CSSProperties, useState } from "react";
import { useIsModern } from "../theme.js";
import { SelectSheet } from "./SelectSheet.js";
import { DateSheet } from "./DateSheet.js";

// "6m" і "12m" лишились у типі лише для сумісності: їх більше ніхто не
// пропонує (Люба, 26.10.2026: «оті півроку, рік прибрать»), але periodTo-
// IsoRange досі вміє їх рахувати, щоб нічого не впало, якщо значення десь
// збережене. Замість них — конкретний місяць ("month" + month).
export type RangePreset = "1m" | "6m" | "12m" | "month" | "all" | "custom";

export interface CustomRange {
  /** YYYY-MM-DD */
  from: string;
  /** YYYY-MM-DD (inclusive end day) */
  to: string;
}

export interface PeriodValue {
  preset: RangePreset;
  custom?: CustomRange;
  /** Для preset = "month": "YYYY-MM". */
  month?: string;
}

/**
 * Перший місяць, за який у боті є касові звіти. Раніше даних просто немає:
 * візити з Bumpix ідуть з 2025-го, але виручку бот знає лише зі звітів
 * майстринь, а перший із них — 25.04.2026. Пропонувати місяці до цього
 * означало б показувати нулі.
 */
export const DATA_START_MONTH = "2026-04";

const MONTH_NAMES = [
  "Січень", "Лютий", "Березень", "Квітень", "Травень", "Червень",
  "Липень", "Серпень", "Вересень", "Жовтень", "Листопад", "Грудень",
];

/** "2026-10" → "Жовтень 2026". */
export function monthTitle(key: string): string {
  const [y, m] = key.split("-").map(Number);
  return `${MONTH_NAMES[m - 1]} ${y}`;
}

/** Поточний місяць за місцевим часом, "YYYY-MM". */
export function currentMonthKey(now = new Date()): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

/** Усі місяці від DATA_START_MONTH до поточного, найновіший першим. */
export function availableMonths(now = new Date()): string[] {
  const out: string[] = [];
  const [sy, sm] = DATA_START_MONTH.split("-").map(Number);
  let y = now.getFullYear();
  let m = now.getMonth() + 1;
  while (y > sy || (y === sy && m >= sm)) {
    out.push(`${y}-${String(m).padStart(2, "0")}`);
    m -= 1;
    if (m === 0) {
      m = 12;
      y -= 1;
    }
  }
  return out;
}

const PRESET_LABELS: Record<Exclude<RangePreset, "custom" | "month">, string> = {
  "1m": "Цей місяць",
  "6m": "Півроку",
  "12m": "Рік",
  all: "Весь час",
};

/** Build {from,to} ISO strings ready to pass to the server. */
export function periodToIsoRange(v: PeriodValue): { from: string; to: string } {
  const now = new Date();
  const to = now.toISOString();
  if (v.preset === "1m") {
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    return { from: start.toISOString(), to };
  }
  if (v.preset === "6m") {
    const start = new Date(now.getTime() - 6 * 30 * 24 * 60 * 60 * 1000);
    return { from: start.toISOString(), to };
  }
  if (v.preset === "12m") {
    const start = new Date(now.getTime() - 12 * 30 * 24 * 60 * 60 * 1000);
    return { from: start.toISOString(), to };
  }
  if (v.preset === "month" && v.month) {
    // Місцева північ 1-го — і до останньої мілісекунди останнього дня. Так
    // само, як "1m" рахує свій початок, тож поточний місяць зі списку і
    // «Цей місяць» дають однакові межі.
    const [y, m] = v.month.split("-").map(Number);
    const start = new Date(y, m - 1, 1);
    const end = new Date(new Date(y, m, 1).getTime() - 1);
    return { from: start.toISOString(), to: end.toISOString() };
  }
  if (v.preset === "all") {
    return { from: "2000-01-01T00:00:00.000Z", to };
  }
  // custom
  if (v.custom?.from && v.custom?.to) {
    // Local midnight → start; end-of-day → end. Avoid TZ surprises.
    const start = new Date(`${v.custom.from}T00:00:00`);
    const end = new Date(`${v.custom.to}T23:59:59`);
    return { from: start.toISOString(), to: end.toISOString() };
  }
  return { from: new Date(0).toISOString(), to };
}

/** Today as YYYY-MM-DD (local). */
function todayLocal(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** A month ago as YYYY-MM-DD (local). */
function monthAgoLocal(): string {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function PeriodPicker({
  value,
  onChange,
}: {
  value: PeriodValue;
  onChange: (v: PeriodValue) => void;
}) {
  // Cache the custom dates locally so users can tap "Свій" → adjust → confirm
  // without losing what they had typed before switching to a preset.
  const [customFrom, setCustomFrom] = useState(
    value.custom?.from ?? monthAgoLocal(),
  );
  const [customTo, setCustomTo] = useState(value.custom?.to ?? todayLocal());
  const [rangeOpen, setRangeOpen] = useState(false);
  const isModern = useIsModern();

  function applyCustom(from: string, to: string) {
    onChange({ preset: "custom", custom: { from, to } });
  }

  if (isModern) {
    const customLabel =
      value.preset === "custom" && value.custom?.from && value.custom?.to
        ? `${fmtShort(value.custom.from)} — ${fmtShort(value.custom.to)}`
        : "Виберіть діапазон";
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 18, marginBottom: 14 }}>
        <SelectSheet<string>
          title="📅 Період"
          value={value.preset === "month" && value.month ? `m:${value.month}` : value.preset}
          onChange={(p) => {
            if (p === "custom") {
              // Якщо ще нема обраного — одразу відкриваємо календар
              if (!value.custom?.from || !value.custom?.to) {
                setRangeOpen(true);
              } else {
                applyCustom(customFrom, customTo);
              }
            } else if (p.startsWith("m:")) {
              onChange({ preset: "month", month: p.slice(2), custom: value.custom });
            } else {
              onChange({ preset: p as RangePreset, custom: value.custom });
            }
          }}
          options={[
            // Поточний місяць — це старий "1m" (з 1-го по сьогодні), тому
            // окремого "m:<поточний>" немає: інакше він був би у списку двічі.
            { value: "1m", label: `📅 ${monthTitle(currentMonthKey())} · поточний` },
            ...availableMonths()
              .slice(1)
              .map((k) => ({ value: `m:${k}`, label: `📅 ${monthTitle(k)}` })),
            { value: "all", label: "📅 Весь час" },
            { value: "custom", label: "📅 Свій інтервал" },
          ]}
        />

        {value.preset === "custom" && (
          <button
            type="button"
            className="nz-sheet-trigger"
            onClick={() => setRangeOpen(true)}
            aria-haspopup="dialog"
          >
            <span className="nz-dropdown-sel">📅 {customLabel}</span>
            <span className="nz-dropdown-caret">▾</span>
          </button>
        )}

        <DateSheet
          mode="range"
          open={rangeOpen}
          onClose={() => setRangeOpen(false)}
          title="📅 Свій інтервал"
          initialFrom={value.custom?.from ?? customFrom}
          initialTo={value.custom?.to ?? customTo}
          maxDate={new Date()}
          onPickRange={(from, to) => {
            setCustomFrom(from);
            setCustomTo(to);
            applyCustom(from, to);
          }}
        />
      </div>
    );
  }

  return (
    <div style={wrapStyle}>
      <div style={rowStyle}>
        <button
          onClick={() => onChange({ preset: "1m", custom: value.custom })}
          style={chipStyle(value.preset === "1m")}
        >
          {PRESET_LABELS["1m"]}
        </button>
        {/* Минулі місяці — випадним списком, а не чипами: їх уже шість і далі
            буде більше, рядок чипів розповзся б на пів екрана. */}
        <select
          value={value.preset === "month" && value.month ? value.month : ""}
          onChange={(e) => {
            if (e.target.value) {
              onChange({ preset: "month", month: e.target.value, custom: value.custom });
            }
          }}
          style={{ ...chipStyle(value.preset === "month"), appearance: "none", WebkitAppearance: "none" }}
        >
          <option value="" disabled>
            Минулий місяць ▾
          </option>
          {availableMonths()
            .slice(1)
            .map((k) => (
              <option key={k} value={k}>
                {monthTitle(k)}
              </option>
            ))}
        </select>
        <button
          onClick={() => onChange({ preset: "all", custom: value.custom })}
          style={chipStyle(value.preset === "all")}
        >
          {PRESET_LABELS.all}
        </button>
        <button
          onClick={() => applyCustom(customFrom, customTo)}
          style={chipStyle(value.preset === "custom")}
        >
          📅 Свій
        </button>
      </div>

      {value.preset === "custom" && (
        <div style={customRowStyle}>
          <label style={labelStyle}>
            з
            <input
              type="date"
              value={customFrom}
              max={customTo}
              onChange={(e) => {
                setCustomFrom(e.target.value);
                applyCustom(e.target.value, customTo);
              }}
              style={inputStyle}
            />
          </label>
          <span style={{ alignSelf: "center", color: "var(--hint)" }}>—</span>
          <label style={labelStyle}>
            по
            <input
              type="date"
              value={customTo}
              min={customFrom}
              max={todayLocal()}
              onChange={(e) => {
                setCustomTo(e.target.value);
                applyCustom(customFrom, e.target.value);
              }}
              style={inputStyle}
            />
          </label>
        </div>
      )}
    </div>
  );
}

/** Compact human-readable description of the active period — for headlines. */
export function periodLabel(v: PeriodValue): string {
  if (v.preset === "month") return v.month ? monthTitle(v.month) : "Місяць";
  if (v.preset === "1m") return monthTitle(currentMonthKey());
  if (v.preset !== "custom") return PRESET_LABELS[v.preset];
  if (v.custom?.from && v.custom?.to) {
    return `${fmtShort(v.custom.from)} — ${fmtShort(v.custom.to)}`;
  }
  return "Свій інтервал";
}

function fmtShort(iso: string): string {
  // "2026-05-28" → "28.05"
  const [, m, d] = iso.split("-");
  return `${d}.${m}`;
}

// ---- styles ----

const wrapStyle: CSSProperties = {
  marginBottom: 12,
};

const rowStyle: CSSProperties = {
  display: "flex",
  gap: 6,
  flexWrap: "wrap",
};

function chipStyle(active: boolean): CSSProperties {
  return {
    padding: "7px 14px",
    border: active
      ? "1px solid transparent"
      : "1px solid rgba(127,127,127,0.25)",
    borderRadius: 999,
    background: active ? "var(--button)" : "var(--secondary-bg)",
    color: active ? "var(--button-text)" : "var(--text)",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    transition: "background 0.15s ease, color 0.15s ease",
  };
}

const customRowStyle: CSSProperties = {
  display: "flex",
  gap: 8,
  marginTop: 10,
  alignItems: "center",
  flexWrap: "wrap",
};

const labelStyle: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 6,
  fontSize: 13,
  color: "var(--hint)",
};

const inputStyle: CSSProperties = {
  padding: "7px 10px",
  borderRadius: 8,
  border: "1px solid rgba(127,127,127,0.3)",
  background: "var(--bg)",
  color: "var(--text)",
  fontSize: 14,
};
